import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import {
  useAnonymizeContact,
  useContact,
  useContacts,
  useContactHistory,
  useCreateContact,
  useDataConnectors,
  useDeleteContact,
  useMergeContacts,
  useUpdateContact,
  type ContactRow,
} from '../api/queries.js';
import { useAuthStore } from '../auth/auth-store.js';
import {
  Button,
  ChannelIcon,
  type ChannelType,
  ConfirmModal,
  DrawerPanel,
  EmptyState,
  Field,
  IconButton,
  InlineTagEditor,
  Input,
  OperatorAvatar,
  StatusChip,
  Tag,
  Textarea,
  type TicketStatus,
  timeAgo,
  formatDate,
} from '../components/ui/index.js';
import { ResizableSplit } from '../components/ui/split.js';

import '../inbox/inbox.css';

export function ContactsPage(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const slug = user?.workspaceSlug;
  const urlSearch = useSearch({ strict: false }) as { id?: string };
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  // Deep-link: открытие конкретного контакта по ?id (из инбокса / глобального поиска).
  // Завязано только на urlSearch.id, чтобы клик по другому контакту его не перетирал.
  useEffect(() => {
    if (urlSearch.id) setSelectedId(urlSearch.id);
  }, [urlSearch.id]);

  const contactsQ = useContacts(slug, search);
  const createContact = useCreateContact(slug);
  const list = contactsQ.data ?? [];
  // Контакт может отсутствовать в текущем (отфильтрованном) списке — догружаем по id.
  const fromList = selectedId ? list.find((c) => c.id === selectedId) : null;
  const directQ = useContact(slug, selectedId && !fromList ? selectedId : undefined);
  const selected = fromList ?? (directQ.data as ContactRow | undefined) ?? null;

  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newNotes, setNewNotes] = useState('');

  function handleCreate(): void {
    if (!newName.trim() && !newEmail.trim()) return;
    createContact.mutate(
      { name: newName || undefined, email: newEmail || undefined, phone: newPhone || undefined },
      {
        onSuccess: (c) => {
          setAddOpen(false);
          setSelectedId(c.id);
          setNewName('');
          setNewEmail('');
          setNewPhone('');
          setNewNotes('');
        },
      },
    );
  }

  return (
    <ResizableSplit id="contacts-cols" direction="row" sizes={[340, 0]} mins={[260, 400]}>
      <aside
        style={{
          borderRight: '1px solid var(--inbx-border)',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <div className="inbx-tb">
          <span className="inbx-tb__title">
            Контакты{' '}
            <span style={{ color: 'var(--inbx-text-tertiary)', fontWeight: 400 }}>
              ({list.length})
            </span>
          </span>
          <div className="inbx-tb__actions">
            <IconButton icon="ti-user-plus" tooltip="Добавить" onClick={() => setAddOpen(true)} />
            <IconButton icon="ti-download" tooltip="Экспорт" />
          </div>
        </div>
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Имя, email, телефон..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div style={{ flex: 1, overflowY: 'auto', borderTop: '1px solid var(--inbx-border)' }}>
          {list.map((c) => (
            <ContactRowItem
              key={c.id}
              contact={c}
              active={c.id === selectedId}
              onClick={() => setSelectedId(c.id)}
            />
          ))}
          {!contactsQ.isLoading && list.length === 0 && (
            <EmptyState title="Контактов нет" description="Создай первый или дождись обращений" />
          )}
        </div>
      </aside>

      {/* Right: detail */}
      <main style={{ overflow: 'auto', background: 'var(--inbx-bg-soft)', height: '100%' }}>
        {selected ? (
          <ContactDetail
            contact={selected}
            workspaceSlug={slug}
            onDelete={() => setSelectedId(null)}
          />
        ) : (
          <EmptyState
            icon="ti-user"
            title="Выберите контакт"
            description="Выбери клиента слева чтобы посмотреть карточку и историю обращений"
          />
        )}

        {/* D1: Create contact drawer */}
        <DrawerPanel
          open={addOpen}
          title="Новый контакт"
          onClose={() => setAddOpen(false)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setAddOpen(false)}>
                Отмена
              </Button>
              <Button
                variant="primary"
                onClick={handleCreate}
                disabled={(!newName.trim() && !newEmail.trim()) || createContact.isPending}
              >
                Создать
              </Button>
            </>
          }
        >
          <Field label="Имя" required>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Иван Петров"
              autoFocus
            />
          </Field>
          <Field label="Email">
            <Input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="i.petrov@mail.ru"
            />
          </Field>
          <Field label="Телефон">
            <Input
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              placeholder="+7 ..."
            />
          </Field>
          <Field label="Заметки">
            <Textarea
              value={newNotes}
              onChange={(e) => setNewNotes(e.target.value)}
              placeholder="Дополнительная информация..."
            />
          </Field>
        </DrawerPanel>
      </main>
    </ResizableSplit>
  );
}

function ContactRowItem({
  contact,
  active,
  onClick,
}: {
  contact: ContactRow;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  const channels = Object.keys(contact.channelIds ?? {}) as ChannelType[];
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        width: '100%',
        gap: 8,
        padding: '10px 12px',
        background: active ? 'var(--inbx-accent-soft)' : 'var(--inbx-bg)',
        borderLeft: active ? '2px solid var(--inbx-accent)' : '2px solid transparent',
        borderBottom: '1px solid var(--inbx-border-tertiary)',
        textAlign: 'left',
      }}
    >
      <OperatorAvatar user={{ id: contact.id, name: contact.name ?? '?' }} size="md" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span
            style={{
              fontWeight: 500,
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {contact.name ?? 'Без имени'}
          </span>
          <span style={{ fontSize: 'var(--inbx-fz-xs)', color: 'var(--inbx-text-tertiary)' }}>
            {timeAgo(contact.lastSeenAt)}
          </span>
        </div>
        <div
          style={{
            fontSize: 'var(--inbx-fz-sm)',
            color: 'var(--inbx-text-tertiary)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {contact.email ?? contact.phone ?? '—'}
        </div>
        <div style={{ display: 'flex', gap: 3, marginTop: 3, alignItems: 'center' }}>
          {channels.slice(0, 4).map((ch) => (
            <ChannelIcon key={ch} channel={ch} size={14} />
          ))}
          {(contact.tags ?? []).slice(0, 2).map((t) => (
            <Tag key={t}>{t}</Tag>
          ))}
        </div>
      </div>
    </button>
  );
}

function ContactDetail({
  contact,
  workspaceSlug,
  onDelete,
}: {
  contact: ContactRow;
  workspaceSlug: string | undefined;
  onDelete: () => void;
}): JSX.Element {
  const navigate = useNavigate();
  const history = useContactHistory(workspaceSlug, contact.id);
  const updateContact = useUpdateContact(workspaceSlug);
  const deleteContact = useDeleteContact(workspaceSlug);
  const anonymizeContact = useAnonymizeContact(workspaceSlug);
  const dataConnectorsQ = useDataConnectors(workspaceSlug);

  const mergeContacts = useMergeContacts(workspaceSlug);
  const [tab, setTab] = useState<'info' | 'history' | 'data'>('info');
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [anonOpen, setAnonOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeSearch, setMergeSearch] = useState('');
  const [mergePick, setMergePick] = useState<ContactRow | null>(null);
  const mergeList = useContacts(workspaceSlug, mergeSearch);

  const [editName, setEditName] = useState(contact.name ?? '');
  const [editEmail, setEditEmail] = useState(contact.email ?? '');
  const [editPhone, setEditPhone] = useState(contact.phone ?? '');

  const attrs = contact.attributes ?? {};
  const channels = Object.keys(contact.channelIds ?? {}) as ChannelType[];

  function handleEdit(): void {
    // Только реально изменённые поля; пустое поле ≡ отсутствие значения (иначе пустой
    // email/phone улетает как null и валит весь патч). Очистку поля шлём как null.
    const patch: Record<string, unknown> = {};
    const nextName = editName.trim();
    const nextEmail = editEmail.trim();
    const nextPhone = editPhone.trim();
    if (nextName !== (contact.name ?? '')) patch.name = nextName || null;
    if (nextEmail !== (contact.email ?? '')) patch.email = nextEmail || null;
    if (nextPhone !== (contact.phone ?? '')) patch.phone = nextPhone || null;
    if (Object.keys(patch).length > 0) updateContact.mutate({ id: contact.id, patch });
    setEditOpen(false);
  }

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: 24 }}>
      <div className="inbx-card" style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
        <OperatorAvatar user={{ id: contact.id, name: contact.name ?? '?' }} size="lg" />
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontSize: 'var(--inbx-fz-xl)' }}>
            {contact.name ?? 'Без имени'}
          </h2>
          <div style={{ color: 'var(--inbx-text-tertiary)', fontSize: 'var(--inbx-fz-sm)' }}>
            {contact.email ?? '—'} {contact.phone ? ` · ${contact.phone}` : ''}
          </div>
          <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
            {channels.map((ch) => (
              <ChannelIcon key={ch} channel={ch} size={18} />
            ))}
          </div>
        </div>
        <Button
          icon="ti-edit"
          variant="secondary"
          onClick={() => {
            setEditName(contact.name ?? '');
            setEditEmail(contact.email ?? '');
            setEditPhone(contact.phone ?? '');
            setEditOpen(true);
          }}
        >
          Редактировать
        </Button>
        <Button icon="ti-arrows-join" variant="secondary" onClick={() => setMergeOpen(true)}>
          Объединить
        </Button>
        <Button icon="ti-trash" variant="ghost" onClick={() => setDeleteOpen(true)}>
          Удалить
        </Button>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 2,
          borderBottom: '1px solid var(--inbx-border)',
          marginBottom: 16,
        }}
      >
        {(['info', 'history', 'data'] as const).map((t) => (
          <button
            key={t}
            type="button"
            className={`inbx-page-header__tab ${tab === t ? 'is-active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t === 'info'
              ? 'Информация'
              : t === 'history'
                ? `История (${history.data?.length ?? 0})`
                : 'Данные'}
          </button>
        ))}
      </div>

      {tab === 'info' && (
        <>
          <div className="inbx-card">
            <h4 className="inbx-card__title">Основное</h4>
            <KV label="Email" value={contact.email ?? '—'} />
            <KV label="Телефон" value={contact.phone ?? '—'} />
            <KV label="Город" value={attrs.city ?? '—'} />
            <KV label="Сегмент" value={attrs.segment ? attrs.segment.toUpperCase() : '—'} />
            <KV label="Создан" value={formatDate(contact.createdAt)} />
            <KV label="Последний контакт" value={timeAgo(contact.lastSeenAt)} />
          </div>
          <div className="inbx-card">
            <h4 className="inbx-card__title">Покупки</h4>
            <KV label="Всего заказов" value={String(attrs.total_orders ?? 0)} />
            <KV
              label="Общая сумма"
              value={
                attrs.total_amount ? `${(attrs.total_amount / 100).toLocaleString('ru-RU')} ₽` : '—'
              }
            />
          </div>
          <div className="inbx-card">
            <h4 className="inbx-card__title">Теги</h4>
            <InlineTagEditor
              value={contact.tags ?? []}
              onChange={(tags) => updateContact.mutate({ id: contact.id, patch: { tags } })}
              placeholder="+ добавить тег"
            />
          </div>
          <div
            className="inbx-card"
            style={{ borderColor: 'var(--inbx-danger)', borderWidth: 1, borderStyle: 'solid' }}
          >
            <h4 className="inbx-card__title" style={{ color: 'var(--inbx-danger)' }}>
              Зона опасности
            </h4>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="ghost" icon="ti-eye-off" onClick={() => setAnonOpen(true)}>
                Анонимизировать (GDPR)
              </Button>
              <Button variant="danger" icon="ti-trash" onClick={() => setDeleteOpen(true)}>
                Удалить контакт
              </Button>
            </div>
          </div>
        </>
      )}

      {tab === 'history' && (
        <div className="inbx-card">
          {(history.data ?? []).map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => void navigate({ to: '/inbox', search: { ticket: t.id } })}
              title="Открыть тикет"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 4px',
                width: '100%',
                textAlign: 'left',
                background: 'transparent',
                border: 'none',
                borderBottom: '1px solid var(--inbx-border-tertiary)',
                cursor: 'pointer',
              }}
            >
              <StatusChip status={t.status as TicketStatus} size="sm" />
              <span style={{ flex: 1, fontSize: 'var(--inbx-fz-md)' }}>
                {t.subject ?? `Тикет #${t.number}`}
              </span>
              <span style={{ color: 'var(--inbx-text-tertiary)', fontSize: 'var(--inbx-fz-sm)' }}>
                {timeAgo(t.createdAt)}
              </span>
              <i className="ti-chevron-right" style={{ color: 'var(--inbx-text-tertiary)' }} />
            </button>
          ))}
          {(history.data ?? []).length === 0 && <EmptyState title="Нет обращений" />}
        </div>
      )}

      {/* D4: Data connectors tab */}
      {tab === 'data' && (
        <div>
          {(dataConnectorsQ.data ?? []).length === 0 ? (
            <div className="inbx-card">
              <EmptyState
                icon="ti-database"
                title="Подключи коннектор данных"
                description="Чтобы видеть заказы клиента из вашего магазина или CRM прямо здесь"
              />
            </div>
          ) : (
            (dataConnectorsQ.data ?? []).map((dc) => (
              <div key={dc.id} className="inbx-card" style={{ marginBottom: 12 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 8,
                  }}
                >
                  <h4 className="inbx-card__title" style={{ margin: 0 }}>
                    {dc.name}
                  </h4>
                  <span
                    style={{ fontSize: 'var(--inbx-fz-xs)', color: 'var(--inbx-text-tertiary)' }}
                  >
                    {dc.kind}
                  </span>
                </div>
                <p
                  style={{
                    margin: 0,
                    color: 'var(--inbx-text-secondary)',
                    fontSize: 'var(--inbx-fz-sm)',
                  }}
                >
                  {dc.description ?? 'Данные будут доступны после настройки коннектора.'}
                </p>
                <Button variant="secondary" size="sm" icon="ti-refresh" style={{ marginTop: 8 }}>
                  Загрузить данные
                </Button>
              </div>
            ))
          )}
        </div>
      )}

      {/* D2: Edit drawer */}
      <DrawerPanel open={editOpen} title="Редактировать контакт" onClose={() => setEditOpen(false)}>
        <Field label="Имя">
          <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
        </Field>
        <Field label="Email">
          <Input type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
        </Field>
        <Field label="Телефон">
          <Input value={editPhone} onChange={(e) => setEditPhone(e.target.value)} />
        </Field>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <Button variant="ghost" onClick={() => setEditOpen(false)}>
            Отмена
          </Button>
          <Button variant="primary" onClick={handleEdit}>
            Сохранить
          </Button>
        </div>
      </DrawerPanel>

      {/* D3: Delete confirm */}
      <ConfirmModal
        open={deleteOpen}
        title="Удалить контакт?"
        description="Это действие необратимо. Все данные контакта будут удалены."
        confirmLabel="Удалить"
        onConfirm={() => {
          deleteContact.mutate(contact.id);
          setDeleteOpen(false);
          onDelete();
        }}
        onCancel={() => setDeleteOpen(false)}
      />

      {/* D3: Anonymize confirm */}
      <ConfirmModal
        open={anonOpen}
        title="Анонимизировать контакт?"
        description="Персональные данные будут заменены на псевдонимы в соответствии с требованиями GDPR/152-ФЗ. Тикеты и история сохранятся без имени и email."
        confirmLabel="Анонимизировать"
        onConfirm={() => {
          anonymizeContact.mutate(contact.id);
          setAnonOpen(false);
        }}
        onCancel={() => setAnonOpen(false)}
      />

      {/* Ручное объединение: выбираем дубликат, который вольётся в текущий контакт */}
      <DrawerPanel
        open={mergeOpen}
        title="Объединить контакты"
        onClose={() => {
          setMergeOpen(false);
          setMergePick(null);
        }}
        width={420}
      >
        <p style={{ fontSize: 'var(--inbx-fz-sm)', color: 'var(--inbx-text-tertiary)' }}>
          Выберите дубликат — его тикеты, сообщения и каналы перейдут в текущий контакт
          <strong> {contact.name ?? contact.email ?? contact.phone ?? contact.id}</strong>, а сам
          дубликат будет помечен объединённым.
        </p>
        <Field label="Поиск контакта">
          <Input
            value={mergeSearch}
            onChange={(e) => setMergeSearch(e.target.value)}
            placeholder="имя, телефон или email"
          />
        </Field>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
          {(mergeList.data ?? [])
            .filter((x) => x.id !== contact.id)
            .slice(0, 12)
            .map((x) => (
              <button
                key={x.id}
                type="button"
                onClick={() => setMergePick(x)}
                style={{
                  textAlign: 'left',
                  padding: '8px 10px',
                  borderRadius: 'var(--inbx-radius)',
                  border: '1px solid var(--inbx-border)',
                  background: 'var(--inbx-bg)',
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontWeight: 500 }}>{x.name ?? 'Без имени'}</div>
                <div style={{ fontSize: 'var(--inbx-fz-sm)', color: 'var(--inbx-text-tertiary)' }}>
                  {x.email ?? x.phone ?? '—'}
                </div>
              </button>
            ))}
          {(mergeList.data ?? []).filter((x) => x.id !== contact.id).length === 0 && (
            <span style={{ color: 'var(--inbx-text-tertiary)', fontSize: 'var(--inbx-fz-sm)' }}>
              Ничего не найдено
            </span>
          )}
        </div>
      </DrawerPanel>

      <ConfirmModal
        open={!!mergePick}
        title="Объединить контакты?"
        description={`«${mergePick?.name ?? mergePick?.email ?? mergePick?.phone ?? ''}» будет влит в текущий контакт. Тикеты и каналы перейдут сюда, дубликат пометится объединённым. Действие необратимо.`}
        confirmLabel="Объединить"
        variant="danger"
        onConfirm={() => {
          if (mergePick) {
            mergeContacts.mutate({ primary_id: contact.id, duplicate_ids: [mergePick.id] });
          }
          setMergePick(null);
          setMergeOpen(false);
        }}
        onCancel={() => setMergePick(null)}
      />
    </div>
  );
}

function KV({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '4px 0',
        fontSize: 'var(--inbx-fz-md)',
      }}
    >
      <span style={{ color: 'var(--inbx-text-tertiary)' }}>{label}</span>
      <span>{value}</span>
    </div>
  );
}
